import React, { useContext, useEffect } from 'react';
import { GridApi } from 'ag-grid-community';
import ExportButton from './ExportButton';
import GridContext from '../../../contexts/GridContext';
import CardContext from '../../../contexts/CardContext';
import ReportContext from '../../../contexts/ReportContext';
import AnalyticsContext from '../../../contexts/AnalyticsContext';
import ConfirmationModal from '../../ConfirmationModal';
import usePopup from '../../../hooks/usePopup';
import isSingleMetricDateMatrix from '../../../types/visTypeCheckers/isSingleMetricDateMatrix';
import delay from '../../../delay';
import useValueFormatters from 'hooks/useValueFormatters';
import visTypeCheckers from 'types/visTypeCheckers';
import useExportGrid from './useExportGrid';
import ExportGridContext from '../context/ExportGridContext';
import useIsLiveBonusPeriodExport from './useIsLiveBonusPeriodExport';
import exportTableExcel from 'components/DataTable/exportTableExcel';
import useGridCsvExport, {
  processCellCallback,
  processSingleMetricDateMatrixExportHeader,
  processSingleMetricDateMatrixHeaderCallback,
} from './useGridCsvExport';

const ExportButtonContainer = ({
  overrideGridApi,
  exportHandle: exportHandleProp,
  exportName,
  isContextMenuItem,
  isTotalsRowIncluded,
}: {
  overrideGridApi?: GridApi;
  // A DataTable's rows instead of a grid
  exportHandle?: DataTable.ExportHandle;
  exportName?: string;
  isContextMenuItem?: boolean;
  // Matrices opt in to exporting their pinned totals row. Boards keep it out.
  isTotalsRowIncluded?: boolean;
}) => {
  const { isOpen, open, close } = usePopup();
  const { i, chartDefinition } = useContext(CardContext);
  const { isExportPending } = useContext(ExportGridContext);
  const {
    gridApi,
    dataType,
    gridQueryOverride,
    exportHandle: gridExportHandle,
  } = useContext(GridContext);
  // A records grid DataTable draws (RecordsTable) publishes its handle on the
  // grid's context, in place of the grid API
  const exportHandle =
    exportHandleProp ??
    (!gridApi && !overrideGridApi ? gridExportHandle : undefined);
  const { setGridExportCallbacks } = useContext(ReportContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { formatValue, formatField } = useValueFormatters();
  const { exportDataAsCsv, getFileName, domId } = useGridCsvExport({
    overrideGridApi,
    exportHandle,
    exportName,
  });
  // Live (not-yet-finalized) bonus periods warn before exporting.
  const isNonClosedBonusExport = useIsLiveBonusPeriodExport();

  const downloadTableAsExcel = async (handle: DataTable.ExportHandle) => {
    const name = exportName || dataType;
    trackEvent('Grid - Exported', {
      name,
    });

    const { loadedRowCount, totalRowCount } = handle;
    const hasAllRows =
      totalRowCount === undefined || totalRowCount === loadedRowCount;
    if (
      hasAllRows ||
      window.confirm(
        `Only the data loaded so far (${loadedRowCount} / ${totalRowCount} items) will be exported.
Please scroll down to the end before exporting to get all data.`,
      )
    ) {
      await exportTableExcel({
        cells: handle.getCells({
          processCell: processCellCallback(formatValue, formatField),
          processHeader: isSingleMetricDateMatrix(chartDefinition)
            ? processSingleMetricDateMatrixExportHeader
            : undefined,
          isTotalsRowIncluded: !!isTotalsRowIncluded,
        }),
        fileName: getFileName(),
      });
    }
  };

  const downloadDataAsCsv = async () => {
    if (exportHandle) {
      await downloadTableAsExcel(exportHandle);
      return;
    }
    if (!gridApi && !overrideGridApi) {
      throw new Error('Grid API missing');
    }
    const gridApiToUse = overrideGridApi
      ? overrideGridApi
      : (gridApi as GridApi);
    const name = exportName || dataType;
    const fileName = getFileName();

    trackEvent('Grid - Exported', {
      name,
    });

    const datasource = gridApiToUse.getGridOption('serverSideDatasource') as
      | DataSource
      | undefined;

    const currentDocs = (() => {
      try {
        return datasource!.documents.length;
      } catch (ex) {
        return '?';
      }
    })();
    const availableDocCount = (() => {
      try {
        return datasource!.searchResult!.count;
      } catch (ex) {
        return '?';
      }
    })();
    const hasAllDocs = (() => {
      try {
        if (gridQueryOverride) {
          return true;
        }

        if (!datasource) {
          return true;
        }

        return datasource.searchResult!.count === currentDocs;
      } catch (ex) {
        return false;
      }
    })();

    if (
      hasAllDocs ||
      window.confirm(
        `Only the data loaded so far (${currentDocs} / ${availableDocCount} items) will be exported.
Please scroll down to the end before exporting to get all data.`,
      )
    ) {
      const buttonDom = document.getElementById(domId);
      if (buttonDom) {
        buttonDom.scrollIntoView(true);
        await delay(1000);
      }
      gridApiToUse.exportDataAsExcel({
        fileName,
        skipPinnedBottom: !isTotalsRowIncluded,
        processCellCallback: processCellCallback(formatValue, formatField),
        processHeaderCallback: isSingleMetricDateMatrix(chartDefinition)
          ? processSingleMetricDateMatrixHeaderCallback
          : undefined,
      });
    }
  };

  const isSimpleGrid =
    visTypeCheckers.isV5ChartDef(chartDefinition) &&
    chartDefinition.simpleGridConfig;

  const { exportSimpleGrid, isDocumentCountExceeded, isDocumentCountLoaded } =
    useExportGrid({
      exportName,
      overrideGridApi,
      exportHandle,
    });

  useEffect(() => {
    if (!i || !setGridExportCallbacks) {
      return;
    }

    setGridExportCallbacks((cbs) => [
      ...cbs.filter((cb) => cb.cardI !== i),
      { cardI: i, callback: exportDataAsCsv },
    ]);
  }, [exportDataAsCsv, i, setGridExportCallbacks]);

  return (
    <>
      <ExportButton
        id={domId}
        isContextMenuItem={isContextMenuItem}
        onClick={
          isNonClosedBonusExport
            ? open
            : isSimpleGrid
              ? exportSimpleGrid
              : downloadDataAsCsv
        }
        disabledMessage={
          isSimpleGrid && !isDocumentCountLoaded
            ? 'Loading, please wait...'
            : isDocumentCountExceeded
              ? 'Gadget is too large to download. Apply filters to export only what you need.'
              : isExportPending
                ? 'Please wait for previous download to complete'
                : undefined
        }
      />
      <ConfirmationModal
        isOpen={isOpen}
        close={close}
        title={'Export Grid?'}
        body={
          'Bonus payments should not be made until the cycle is closed and signed off'
        }
        confirmText="Export"
        onConfirmed={downloadDataAsCsv}
      />
    </>
  );
};

export default ExportButtonContainer;
